/**
 * Light or dark, as the person chose on their profile page (`theme` in their
 * settings), or the device's own when they chose `system`.
 *
 * The page's dark tokens hang off `data-theme="dark"` on the root, which this
 * sets — never a `prefers-color-scheme` query in CSS, or an override could not
 * win against it. The choice is also kept in this browser so the first paint,
 * before `/api/me` answers (and the sign-in page, where it never does), is
 * already the right one.
 */
import { ref } from "vue";
import { THEMES, type Theme } from "@shared/settings.js";

const KEY = "todos.theme";
const deviceDark = window.matchMedia("(prefers-color-scheme: dark)");
let chosen: Theme = "system";

/** Whether the page is dark now, for what CSS cannot reach (the file editor's Ace theme). */
export const darkTheme = ref(false);

function paint(): void {
  const dark = chosen === "dark" || (chosen === "system" && deviceDark.matches);
  document.documentElement.dataset.theme = dark ? "dark" : "light";
  darkTheme.value = dark;
}

export function setTheme(theme: Theme): void {
  chosen = theme;
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // Storage blocked: it only costs the first paint.
  }
  paint();
}

/** Paint the theme this browser last had. Called once, before mounting. */
export function initTheme(): void {
  try {
    const kept = localStorage.getItem(KEY);
    if ((THEMES as readonly (string | null)[]).includes(kept)) chosen = kept as Theme;
  } catch {
    // As above.
  }
  deviceDark.addEventListener("change", paint);
  paint();
}
